import React, { useEffect, useRef, useState } from 'react'
import Button from 'components/button'
import Editor from 'components/editor'
import Field from 'components/field'
import { Plus } from 'components/icons'
import { isEditorEmpty } from 'components/spreadsheet/helpers/functions'
import { EditorContent } from 'types'

interface ColumnDescriptionFieldProps {
  description: EditorContent | null | undefined
  editorId: string
  tablePublicId: string
  onChange: (content: EditorContent) => void
}

// A column's description, shared by the add- and edit-column panels. Most columns don't have one, and a
// rich-text editor is tall in a panel this narrow, so an empty description sits behind an "Add
// description" link until asked for. Once shown it stays shown — clearing the text mid-edit mustn't
// whisk the editor away — so callers key it by column to start each column afresh.
const ColumnDescriptionField: React.FC<ColumnDescriptionFieldProps> = ({
  description,
  editorId,
  tablePublicId,
  onChange
}) => {
  const [open, setOpen] = useState(() => !!description && !isEditorEmpty(description))
  const focusOnOpen = useRef(false)
  const editorRef = useRef<HTMLDivElement>(null)

  // Revealing the editor is a request to type in it, so put the caret there rather than make the user
  // click a second time. Only after the link is clicked — an existing description mustn't steal focus.
  useEffect(() => {
    if (!open || !focusOnOpen.current) return
    focusOnOpen.current = false
    editorRef.current?.querySelector<HTMLElement>('[contenteditable="true"]')?.focus()
  }, [open])

  if (!open) {
    return (
      <div className="px-2.5 pt-2">
        <Button
          internalType="transparent"
          className="h-auto flex items-center gap-1 py-1 px-0 text-sm text-blue hover:bg-transparent hover:underline"
          data-testid="add-column-description"
          onClick={() => {
            focusOnOpen.current = true
            setOpen(true)
          }}
        >
          <Plus />
          Add description
        </Button>
      </div>
    )
  }

  return (
    <Field label="Description">
      <div ref={editorRef}>
        <Editor
          databaseDoc={description}
          readOnly={false}
          editorId={editorId}
          border
          className="rounded-lg bg-surface-faint !border-divider text-sm"
          onChange={onChange}
          resources={[{ resource: 'document_table', publicId: tablePublicId }]}
        />
      </div>
    </Field>
  )
}

export default ColumnDescriptionField
